import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ArtworkPicker } from '../../components/ui/ArtworkPicker';
import { Card, Chip } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { RepostButton } from '../../components/ui/RepostButton';
import { SendToRoom } from '../rooms/SendToRoom';
import { ApiError, api } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import { Waveform } from '../player/Waveform';
import { CommentThread } from '../comments/CommentThread';
import { Lyrics } from './Lyrics';
import { TrackVideo } from './TrackVideo';
import { ProvenanceNote } from './provenance';
import { ShareEmbed } from './ShareEmbed';
import { DownloadOriginal } from '../moderation/DownloadOriginal';
import { ReportButton } from '../moderation/ReportDialog';
import { comments as commentsApi } from '../comments/api';
import { CreditsEditor } from './CreditsEditor';
import { CreditsList } from './CreditsList';
import { type TrackDetail, toPlayable } from './detail';
import { TrackEditor } from './TrackEditor';
import { AddToPlaylist } from '../library/AddToPlaylist';
import { describeKey } from './keys';
import { useDocumentTitle } from '../../lib/title';

export function TrackPage() {
  const { id = '' } = useParams();

  const current = usePlayer((s) => s.track);
  const playing = usePlayer((s) => s.playing);
  const position = usePlayer((s) => s.position);
  const duration = usePlayer((s) => s.duration);
  const play = usePlayer((s) => s.play);
  const toggle = usePlayer((s) => s.toggle);
  const seek = usePlayer((s) => s.seek);

  const [editing, setEditing] = useState(false);
  const [editingCredits, setEditingCredits] = useState(false);

  // The same query key the thread uses, so react-query serves both the pins
  // here and the list below from one request.
  const { data: thread } = useQuery({
    queryKey: ['comments', Number(id)],
    queryFn: () => commentsApi.forTrack(Number(id)),
    enabled: id !== '',
  });


  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['track', id],
    queryFn: () => api.get<TrackDetail>(`/tracks/${id}`),
    retry: false,
    /*
     * While it is still transcoding, keep asking.
     *
     * This is the "uploaded but not there yet" state again, and on a track it
     * lasts minutes rather than the second an avatar takes. Polling stops as
     * soon as it resolves either way. Phase 4 replaces it with a `track.ready`
     * event on `private-user.{id}` (§8.1).
     */
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'processing' || status === 'pending' ? 3000 : false;
    },
  });

  useDocumentTitle(
    data ? [data.title, data.owner?.display_name ?? data.owner?.username].filter(Boolean).join(' · ') : null,
  );

  if (isPending) {
    return <div className="mx-auto max-w-3xl px-6 py-16 text-soft">Loading…</div>;
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404;

    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Alert tone={missing ? 'info' : 'error'}>
          {missing ? 'That track does not exist, or is not public.' : 'Could not load that track.'}
        </Alert>
      </div>
    );
  }

  const isCurrent = current?.id === data.id;
  const total = isCurrent ? duration || (data.duration_ms ?? 0) / 1000 : (data.duration_ms ?? 0) / 1000;
  const progress = isCurrent && total > 0 ? Math.min(1, position / total) : 0;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-10">
      {/*
            Stacked on a phone, side by side once there is room.

            `flex-wrap` cannot save this on its own: the details beside the
            cover are `flex-1`, so instead of wrapping to the next line they
            shrink to whatever is left - 146px at 390, and 129px at 325. The
            title broke over three lines, the embed box became a slot, and the
            whole right-hand side read as a column of debris. Wrapping only
            happens for an item that refuses to shrink; this one is built to.
          */}
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:flex-wrap">
        <ArtworkPicker
          subject="tracks"
          subjectId={data.id}
          artwork={data.artwork}
          refetch={() => api.get<TrackDetail>(`/tracks/${data.id}`)}
          editable={data.is_owner}
          onUploaded={() => void refetch()}
        />

        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{data.title}</h1>

          {data.owner && (
            <Link to={`/${data.owner.username}`} className="meta mt-2 inline-block text-accent hover:underline">
              @{data.owner.username}
            </Link>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {data.visibility !== 'public' && <Chip>{data.visibility}</Chip>}
            {data.status === 'ready' && (
              <span className="meta text-faint">
                {data.play_count.toLocaleString()} plays
              </span>
            )}
          </div>

          {data.description && (
            <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed text-soft">
              {data.description}
            </p>
          )}

          <div className="mt-4">
            <CreditsList credits={data.credits} />
          </div>

          {/*
            Sharing, on a public and ready track only.
            Offering an embed code for something still encoding, or for a track
            nobody else can open, hands somebody a snippet that renders "not
            available" on their blog.
          */}
          {data.status === 'ready' && data.visibility === 'public' && (
            <div className="mt-6 border-t border-line pt-5">
              <span className="meta-caps text-faint">Share</span>
              <div className="mt-3">
                <ShareEmbed trackId={data.id} title={data.title} />
              </div>
            </div>
          )}

          {/* Understated, and not offered on your own track - reporting
              yourself is not a thing anybody means to do. */}
          {!data.is_owner && (
            <div className="mt-4 flex flex-col gap-3">
              <ReportButton subjectType="track" subjectId={data.id} title={data.title} />
              {/* Moderation, beside the other thing a stranger can do about a track. */}
              <DownloadOriginal trackId={data.id} owned={data.is_owner} />
            </div>
          )}

          {data.is_owner && (
            <div className="mt-4 -ml-2 flex flex-wrap gap-1">
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Edit track
              </Button>
              <Button variant="ghost" onClick={() => setEditingCredits(true)}>
                Credits
              </Button>
            </div>
          )}
        </div>
      </div>

      {/*
        The four upload states, each said plainly. `processing` is the one
        people find confusing - the file is uploaded and the track is not
        playable yet - and on audio it lasts minutes, so it has to be explicit
        rather than a spinner.
      */}
      {data.status === 'processing' || data.status === 'pending' ? (
        <Alert tone="info">
          Processing. We are building the streaming versions. This usually takes a minute or two, and the page
          will update itself.
        </Alert>
      ) : data.status === 'failed' ? (
        <Alert tone="error">{data.failure_reason ?? 'Processing failed.'}</Alert>
      ) : (
        <Card className="flex flex-col gap-4 p-5">
          {/*
            Wraps, and the waveform takes its own line on a phone.

            Without this the row had no give: the last button was squeezed to
            76px and broke over four lines, and the waveform - which is the
            point of the row - was allotted zero width. Buttons keep their size
            and move down instead.
          */}
          <div className="flex flex-wrap items-center gap-4">
            <Button
              onClick={() => {
                if (isCurrent) {
                  toggle();
                } else {
                  void play(toPlayable(data), undefined, 'track');
                }
              }}
            >
              {isCurrent && playing ? 'Pause' : 'Play'}
            </Button>

            <FavoriteButton
              trackId={data.id}
              favorited={data.favorited}
              likeCount={data.like_count}
              showCount
            />

            <RepostButton
              title={data.title}
              artist={data.owner?.display_name ?? null}
              trackId={data.id}
              reposted={data.reposted}
              repostCount={data.repost_count}
              showCount
              // Rewinding is publishing to your followers, so it only applies
              // to something they can hear. The server refuses anything else;
              // this stops the button offering it.
              disabled={data.visibility !== 'public'}
            />

            {/*
              Filing it, next to keeping it and sharing it.

              This page had every other verb and not this one, so the only way
              to put a track on a playlist was to play it first and use the
              dock - which is a detour on the one screen entirely about this
              track.
            */}
            <AddToPlaylist trackId={data.id} title={data.title} size="sm" />

            {/*
              The action the earned boost is built around, at the moment
              somebody decides a track deserves an audience - which is while
              they are listening to it, not next time they open a room.

              Public only, for the same reason as rewinding: a room cannot play
              what its listeners are not allowed to hear, and the server
              refuses it. Offering it and then failing explains the rule worse
              than not offering it.
            */}
            {data.visibility === 'public' && (
              <SendToRoom trackId={data.id} ownedByMe={data.is_owner} />
            )}

            <Waveform
              url={data.waveform_url}
              progress={progress}
              height={56}
              onSeek={isCurrent ? (fraction) => seek(fraction * total) : undefined}
              // A full line of its own on a phone, a column beside the
              // buttons once there is room. Sharing a row with them left it
              // the few pixels they had not taken.
              className="w-full min-w-0 lg:w-auto lg:flex-1"
              // §6.3's waveform pins: comments anchored to a moment, shown
              // where they were left.
              pins={thread?.pins ?? []}
              durationMs={data.duration_ms}
              onPinClick={(pin) => seek(pin.position_ms / 1000)}
            />
          </div>

          {/*
            The video, when there is one, under the player and above the words.
            A picture until it is pressed; see `TrackVideo`.
          */}
          {data.video && (
            <div className="mt-5">
              <TrackVideo video={data.video} title={data.title} />
            </div>
          )}

          {/*
            The words, above the technical facts rather than below them. Lyrics
            are the reason somebody scrolls; loudness and tempo are reference.
          */}
          {typeof data.lyrics === 'string' && data.lyrics !== '' && <Lyrics text={data.lyrics} />}

          {/*
            Always drawn, including when nothing was stated, which is the point:
            an absent line would let silence read as "performed". See
            `ProvenanceNote`.
          */}
          <ProvenanceNote value={data.provenance ?? null} />

          {(data.bpm !== null || data.key_signature !== null) && (
            /*
              Beside the loudness rather than in the description, because it is
              the same kind of fact: something true of the recording that a
              listener may want and an artist should not have to write out.

              Only shown when there is something to show. An empty "— BPM" line
              on every track that skipped the field would be worse than silence.
            */
            <div className="meta text-faint" data-track-tempo>
              {[
                data.bpm === null ? null : `${data.bpm} BPM`,
                data.key_signature === null ? null : describeKey(data.key_signature),
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}

          {data.loudness_lufs !== null && (
            /*
               Shown because it explains something rather than decorates.
               Everything is normalised to -14 LUFS so the charts are not a
               volume war (§4.2), and an artist seeing their master measured at
               -7 understands why it is not louder than everybody else's.
            */
            <div className="meta text-faint">
              source loudness {data.loudness_lufs} LUFS &middot; normalized to -14 for playback
            </div>
          )}
        </Card>
      )}

      {data.description && (
        <Card className="p-5">
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-text-2">{data.description}</p>
        </Card>
      )}

      {data.status === 'ready' && <CommentThread trackId={data.id} />}

      {editingCredits && (
        <CreditsEditor
          trackId={data.id}
          credits={data.credits}
          onClose={() => setEditingCredits(false)}
          onSaved={() => {
            setEditingCredits(false);
            void refetch();
          }}
        />
      )}

      {editing && (
        <TrackEditor
          track={data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refetch();
          }}
        />
      )}
    </div>
  );
}
